import PromotionConditionType from '../types/promotionConditionType.js'

const toIntId = (value) => {
  const n = Number.parseInt(String(value), 10)
  return Number.isFinite(n) ? n : null
}

const round2 = (n) => Math.round(Number(n) * 100) / 100

export const validateConditionMetaData = (typeId, metaData) => {
  if (metaData === null || metaData === undefined || typeof metaData !== 'object') {
    return { valid: false, error: 'meta_data must be an object' }
  }
  if (typeId === PromotionConditionType.CONDITIONAL) {
    const minValue = metaData.minValue
    if (minValue === null || minValue === undefined || Number(minValue) <= 0) {
      return { valid: false, error: 'minValue is required and must be a positive number' }
    }
  }
  if (typeId === PromotionConditionType.COMBINED) {
    if (!Array.isArray(metaData.productIds) || metaData.productIds.length === 0) {
      return { valid: false, error: 'productIds is required and must be a non-empty array' }
    }
    for (const productId of metaData.productIds) {
      if (productId === null || Number(productId) <= 0) {
        return { valid: false, error: 'each productIds entry must be a positive number' }
      }
    }
    const count = metaData.count
    if (count === null || count === undefined || Number.parseInt(String(count), 10) <= 0) {
      return { valid: false, error: 'count is required and must be a positive integer' }
    }
  }
  if (typeId === PromotionConditionType.PROMO_CODE) {
    const code = metaData.code
    if (code === null || code === undefined || String(code).trim().length === 0) {
      return { valid: false, error: 'code is required and must be a non-empty string' }
    }
  }
  return { valid: true }
}

export const normalizePromoCode = (code) => String(code ?? '').trim().toLowerCase()

export const sumNonRewardSubtotal = (products, productsDetailsMap, toInt = toIntId) => {
  let sum = 0
  for (const line of products) {
    if (line.promotionId != null && line.promotionId !== '') continue
    const pid = toInt(line.productId)
    if (pid === null) continue
    const details = productsDetailsMap.get(pid)
    const unit = parseFloat(details?.price ?? 0)
    sum += unit * Number(line.quantity ?? 0)
  }
  return round2(sum)
}

export const sumNonRewardSubtotalExcludingProductIds = (
  products,
  productsDetailsMap,
  excludeProductIds,
  toInt = toIntId
) => {
  const exclude =
    excludeProductIds instanceof Set ? excludeProductIds : new Set(excludeProductIds ?? [])
  let sum = 0
  for (const line of products) {
    if (line.promotionId != null && line.promotionId !== '') continue
    const pid = toInt(line.productId)
    if (pid === null || exclude.has(pid)) continue
    const details = productsDetailsMap.get(pid)
    const unit = parseFloat(details?.price ?? 0)
    sum += unit * Number(line.quantity ?? 0)
  }
  return round2(sum)
}

export const combinedConditionProductIdSet = (conditions) => {
  const list = Array.isArray(conditions) ? conditions : []
  const ids = new Set()
  for (const cond of list) {
    const typeId = cond.typeId ?? cond.type_id
    if (typeId !== PromotionConditionType.COMBINED) continue
    const meta = cond.metaData ?? cond.meta_data ?? {}
    for (const raw of meta.productIds ?? []) {
      const pid = toIntId(raw)
      if (pid !== null) ids.add(pid)
    }
  }
  return ids
}

export const countQualifyingUnitsForCombined = (products, productIdSet, toInt = toIntId) => {
  let n = 0
  for (const line of products) {
    if (line.promotionId != null && line.promotionId !== '') continue
    const pid = toInt(line.productId)
    if (pid === null || !productIdSet.has(pid)) continue
    n += Number(line.quantity ?? 0)
  }
  return n
}

export const totalRewardQuantityForPromotion = (products, promotionId, toInt = toIntId) => {
  const prom = toInt(promotionId)
  if (prom === null) return 0
  let n = 0
  for (const line of products) {
    if (toInt(line.promotionId) !== prom) continue
    n += Number(line.quantity ?? 0)
  }
  return n
}

/**
 * Max total gift units for this promotion from cart (non-reward lines only), matching backend scaling.
 * @param {object} promotion — with conditions[]
 * @param {Array<{ productId: *, quantity: *, promotionId?: * }>} baseOrderLines — lines excluding current promotion rewards
 */
export const computeMaxGiftUnitsForPromotion = (promotion, baseOrderLines, productsDetailsMap) => {
  const conds = promotion?.conditions ?? []
  if (!Array.isArray(conds) || conds.length === 0) {
    return Number.POSITIVE_INFINITY
  }
  const combinedListIds = combinedConditionProductIdSet(conds)
  let max = Number.POSITIVE_INFINITY
  for (const cond of conds) {
    const typeId = cond.typeId ?? cond.type_id
    const meta = cond.metaData ?? cond.meta_data ?? {}
    const metaCheck = validateConditionMetaData(typeId, meta)
    if (!metaCheck.valid) continue
    if (typeId === PromotionConditionType.CONDITIONAL) {
      const minValue = Number(meta.minValue)
      const subtotal =
        combinedListIds.size > 0
          ? sumNonRewardSubtotalExcludingProductIds(baseOrderLines, productsDetailsMap, combinedListIds)
          : sumNonRewardSubtotal(baseOrderLines, productsDetailsMap)
      if (minValue > 0) {
        max = Math.min(max, Math.floor(subtotal / minValue + 1e-9))
      }
    } else if (typeId === PromotionConditionType.COMBINED) {
      const perGift = Number.parseInt(String(meta.count), 10)
      const idSet = new Set(meta.productIds.map((id) => toIntId(id)).filter((id) => id !== null))
      const got = countQualifyingUnitsForCombined(baseOrderLines, idSet)
      if (perGift > 0) {
        max = Math.min(max, Math.floor(got / perGift + 1e-9))
      }
    }
  }
  if (!Number.isFinite(max) || max < 0) return 0
  return max
}

/**
 * @param {Array} conditions
 * @param {{ products: Array<{ productId: *, quantity: *, promotionId?: * }>, productsDetailsMap: Map<number, { price?: * }>, promoCode?: string, promotionId?: number }} ctx
 */
export const evaluatePromotionConditions = (conditions, { products, productsDetailsMap, promoCode, promotionId }) => {
  const list = Array.isArray(conditions) ? conditions : []
  if (list.length === 0) {
    return { ok: true }
  }

  const rewardUnits =
    promotionId != null ? totalRewardQuantityForPromotion(products, promotionId) : 0
  const giftScale = Math.max(1, rewardUnits)

  const combinedListIds = combinedConditionProductIdSet(list)

  for (const cond of list) {
    const typeId = cond.typeId ?? cond.type_id
    const meta = cond.metaData ?? cond.meta_data ?? {}
    const metaCheck = validateConditionMetaData(typeId, meta)
    if (!metaCheck.valid) {
      return { ok: false, error: metaCheck.error }
    }

    if (typeId === PromotionConditionType.CONDITIONAL) {
      const minValue = Number(meta.minValue)
      const subtotal =
        combinedListIds.size > 0
          ? sumNonRewardSubtotalExcludingProductIds(products, productsDetailsMap, combinedListIds)
          : sumNonRewardSubtotal(products, productsDetailsMap)
      const required = minValue * giftScale
      if (subtotal < required) {
        return {
          ok: false,
          error: `Нужна сумма других товаров не меньше ${required} сом (${minValue} за подарок × ${giftScale} шт.)`,
        }
      }
    } else if (typeId === PromotionConditionType.COMBINED) {
      const perGift = Number.parseInt(String(meta.count), 10)
      const idSet = new Set(meta.productIds.map((id) => toIntId(id)).filter((id) => id !== null))
      const got = countQualifyingUnitsForCombined(products, idSet)
      const need = perGift * giftScale
      if (got < need) {
        return {
          ok: false,
          error: `Нужно не меньше ${need} подходящих товаров из списка условия (${perGift} за подарок × ${giftScale} шт.)`,
        }
      }
    } else if (typeId === PromotionConditionType.PROMO_CODE) {
      const expected = normalizePromoCode(meta.code)
      const given = normalizePromoCode(promoCode)
      if (given !== expected) {
        return { ok: false, error: 'Промокод не совпадает или не введён' }
      }
    }
  }

  return { ok: true }
}

export function cartItemsToOrderLines(items) {
  return (items || []).map((it) => ({
    productId: it.id,
    quantity: it.quantity,
    ...(it.promotionId != null && it.promotionId !== '' ? { promotionId: it.promotionId } : {}),
  }))
}

export function buildProductsDetailsMap(products) {
  const map = new Map()
  for (const p of products || []) {
    const id = p?.id ?? p?._id
    const n = toIntId(id)
    if (n === null) continue
    map.set(n, { price: p.price, name: p.name, imageUrl: p.image ?? p.imageUrl })
  }
  return map
}

export function unitPriceAfterDiscount(basePrice, discountPercent) {
  const b = Number(basePrice)
  let d = discountPercent != null && discountPercent !== '' ? Number(discountPercent) : 100
  if (!Number.isFinite(d) || d < 0) d = 0
  if (d > 100) d = 100
  return round2(b * (1 - d / 100))
}
